import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { FaChevronDown } from 'react-icons/fa';
import { FiSearch, FiShoppingBag } from "react-icons/fi";
import { HiOutlineUser } from "react-icons/hi";
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const SEARCH_DATA = [
    { title: 'Home', path: '/', description: 'Welcome to OFFF THE ROAST', keywords: ['home', 'roast', 'coffee', 'welcome', 'experience'] },
    { title: 'Booking', path: '/booking', description: 'Book a coffee roasting session', keywords: ['booking', 'book', 'reserve', 'session', 'roasting', 'espresso', 'latte', 'cappuccino', 'drip'] },
    { title: 'Our Story', path: '/our-story', description: 'Learn about our journey and team', keywords: ['story', 'about', 'team', 'jacob', 'hong', 'tom', 'journey', 'founder'] },
    { title: 'Contact', path: '/contact', description: 'Get in touch with us', keywords: ['contact', 'email', 'phone', 'location', 'address', 'reach'] },
    { title: 'Gallery', path: '/gallery', description: 'Photos and videos of our roasting process', keywords: ['gallery', 'media', 'photos', 'videos', 'images', 'roasting'] },
    { title: 'Sign Up', path: '/sign-up', description: 'Create an account', keywords: ['sign up', 'register', 'account', 'login', 'join'] },
    { title: 'Privacy Policy', path: '/privacy-policy', description: 'Our privacy policy', keywords: ['privacy', 'policy', 'data', 'information'] },
    { title: 'Terms of Service', path: '/terms-of-service', description: 'Terms of service', keywords: ['terms', 'service', 'agreement'] },
    { title: 'Terms of Use', path: '/terms-of-use', description: 'Terms of use', keywords: ['terms', 'use', 'conditions'] },
    { title: 'Career', path: '/career', description: 'Join our team', keywords: ['career', 'jobs', 'hiring', 'work', 'business development', 'apply'] },
    { title: 'Our Menu', path: '/menu', description: 'Our coffee and specialty drinks menu', keywords: ['menu', 'drinks', 'espresso', 'latte', 'cappuccino', 'cold brew', 'pour over', 'prices', 'sandwich', 'food'] },
    { title: 'Whole Bean', path: '/our-coffee', description: 'Shop our whole bean coffee selection', keywords: ['shop', 'coffee', 'beans', 'whole bean', 'roast', 'buy', 'purchase'] },
    { title: 'Coffee Roaster', path: '/coffee-roaster', description: 'Browse coffee equipment', keywords: ['shop', 'equipment', 'tools', 'gear', 'grinder', 'brewer', 'roaster'] },
    { title: 'Merch', path: '/merch', description: 'Shop OFFF THE ROAST apparel and merch', keywords: ['shop', 'merch', 'merchandise', 'apparel', 'tshirt', 't-shirt', 'tee', 'shirt', 'clothing'] },
    { title: 'Blog', path: '/blog', description: 'Coffee stories, origins, and education', keywords: ['blog', 'articles', 'journal', 'stories', 'origins', 'education', 'coffee tips', 'roasting class', 'yemen', 'ethiopia'] },
];

function Navbar() {
    const [click, setClick] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [openDropdown, setOpenDropdown] = useState(null);
    const searchRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const { cartCount, openDrawer } = useCart();
    const { currentUser, isAdmin, logout } = useAuth();
    const isAdminUser = currentUser && isAdmin && currentUser.email === 'info@offftheroast.com';

    const handleClick = () => {
        if (!click) {
            window.scrollTo(0, 0);
        }
        setClick(!click);
    };
    const closeMobileMenu = () => {
        setClick(false);
        setOpenDropdown(null);
        window.scrollTo(0, 0);
    };
    const toggleDropdown = (name) => {
        setOpenDropdown(openDropdown === name ? null : name);
    };
    const toggleSearch = () => {
        if (searchOpen) {
            setSearchQuery('');
            setSearchResults([]);
        }
        setSearchOpen(!searchOpen);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (query.trim() === '') {
            setSearchResults([]);
            return;
        }
        const lower = query.toLowerCase();
        const results = SEARCH_DATA.filter(item =>
            item.title.toLowerCase().includes(lower) ||
            item.description.toLowerCase().includes(lower) ||
            item.keywords.some(k => k.includes(lower))
        );
        setSearchResults(results);
    };

    const handleResultClick = (path) => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        navigate(path);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 80) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSearchOpen(false);
                setSearchQuery('');
                setSearchResults([]);
            }
        };

        if (searchOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [searchOpen]);

    useEffect(() => {
        const handleMenuClickOutside = (event) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target) &&
                !event.target.closest('.menu-icon')
            ) {
                setClick(false);
            }
        };

        if (click) {
            document.addEventListener("mousedown", handleMenuClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleMenuClickOutside);
        };
    }, [click]);

    return (
        <>
            <nav className={scrolled ? "navbar navbar-scrolled" : "navbar"}>
                <div className="navbar-container">
                    <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
                        <img src="/images/logo-white.png" alt="logo" className="navbar-logo-img" />
                    </Link>
                    <div className='menu-icon' onClick={handleClick}>
                        <i className={click ? 'fas fa-times' : 'fas fa-bars'} />
                    </div>
                    <ul ref={menuRef} className={click ? 'nav-menu active' : 'nav-menu'}>
                        <li className='nav-item'>
                            <Link to='/' className='nav-links' onClick={closeMobileMenu}>
                                Home
                            </Link>
                        </li>
                        <li className={`nav-item nav-item-dropdown ${openDropdown === 'booking' ? 'dropdown-open' : ''}`}>
                            <Link to='/booking' className='nav-links nav-links-dropdown' onClick={(e) => {
                                if (window.innerWidth <= 960) {
                                    e.preventDefault();
                                    toggleDropdown('booking');
                                } else {
                                    closeMobileMenu();
                                }
                            }}>
                                Booking <FaChevronDown className='dropdown-arrow' />
                            </Link>
                            <ul className='dropdown-menu'>
                                <li>
                                    <Link to='/booking' className='dropdown-link' onClick={closeMobileMenu}>
                                        Private Class
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/team-building' className='dropdown-link' onClick={closeMobileMenu}>
                                        Team Building
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/booking' className='dropdown-link' onClick={closeMobileMenu}>
                                        Pop-Up Service
                                    </Link>
                                </li>
                            </ul>
                        </li>
                        <li className={`nav-item nav-item-dropdown ${openDropdown === 'shop' ? 'dropdown-open' : ''}`}>
                            <span className='nav-links nav-links-dropdown' onClick={() => toggleDropdown('shop')}>
                                Shop <FaChevronDown className='dropdown-arrow' />
                            </span>
                            <ul className='dropdown-menu'>
                                <li>
                                    <Link to='/menu' className='dropdown-link' onClick={closeMobileMenu}>
                                        Our Menu
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/our-coffee' className='dropdown-link' onClick={closeMobileMenu}>
                                        Whole Bean
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/coffee-roaster' className='dropdown-link' onClick={closeMobileMenu}>
                                        Coffee Roaster
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/merch' className='dropdown-link' onClick={closeMobileMenu}>
                                        Merch
                                    </Link>
                                </li>
                            </ul>
                        </li>
                        <li className={`nav-item nav-item-dropdown ${openDropdown === 'about' ? 'dropdown-open' : ''}`}>
                            <span className='nav-links nav-links-dropdown' onClick={() => toggleDropdown('about')}>
                                About <FaChevronDown className='dropdown-arrow' />
                            </span>
                            <ul className='dropdown-menu'>
                                <li>
                                    <Link to='/our-story' className='dropdown-link' onClick={closeMobileMenu}>
                                        Our Story
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/contact' className='dropdown-link' onClick={closeMobileMenu}>
                                        Contact
                                    </Link>
                                </li>
                            </ul>
                        </li>
                        <li className={`nav-item nav-item-dropdown ${openDropdown === 'media' ? 'dropdown-open' : ''}`}>
                            <span className='nav-links nav-links-dropdown' onClick={() => toggleDropdown('media')}>
                                Media <FaChevronDown className='dropdown-arrow' />
                            </span>
                            <ul className='dropdown-menu'>
                                <li>
                                    <Link to='/gallery' className='dropdown-link' onClick={closeMobileMenu}>
                                        Videos
                                    </Link>
                                </li>
                                <li>
                                    <Link to='/blog' className='dropdown-link' onClick={closeMobileMenu}>
                                        Blog
                                    </Link>
                                </li>
                                <li>
                                    <a
                                        href='https://jazzgroove.org/stream?channel=mix1'
                                        className='dropdown-link'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={closeMobileMenu}
                                    >
                                        Jazz Groove
                                    </a>
                                </li>
                            </ul>
                        </li>
                        <li className='nav-item'>
                            <Link to='/career' className='nav-links' onClick={closeMobileMenu}>
                                Career
                            </Link>
                        </li>
                        <li className='nav-item mobile-login-item'>
                            {currentUser ? (
                                <span className='nav-links' onClick={() => { logout(); closeMobileMenu(); }}>
                                    Log out
                                </span>
                            ) : (
                                <Link to='/sign-up' className='nav-links' onClick={closeMobileMenu}>
                                    Log in
                                </Link>
                            )}
                        </li>
                        <li className='mobile-social-icons'>
                            <a href='https://www.instagram.com/offf_the_roast_official/' target='_blank' rel='noopener noreferrer'><i className='fab fa-instagram' /></a>
                            <a href='https://www.facebook.com/huang.jacob.125' target='_blank' rel='noopener noreferrer'><i className='fab fa-facebook-f' /></a>
                            <a href='https://www.youtube.com/@offftheroast' target='_blank' rel='noopener noreferrer'><i className='fab fa-youtube' /></a>
                        </li>
                    </ul>
                    <div className='nav-icons'>
                        {currentUser ? (
                            <>
                                <div className='nav-account-wrapper'>
                                    <span className='nav-account-trigger'>
                                        {isAdminUser ? 'Admin Portal' : 'My Account'}
                                    </span>
                                    <ul className='nav-account-dropdown'>
                                        <li>
                                            <Link to='/my-account' onClick={closeMobileMenu}>
                                                {isAdminUser ? 'Admin Portal' : 'My Account'}
                                            </Link>
                                        </li>
                                        {!isAdminUser && (
                                            <li>
                                                <Link to='/my-account?tab=orders' onClick={closeMobileMenu}>
                                                    My Orders
                                                </Link>
                                            </li>
                                        )}
                                        <li>
                                            <span onClick={() => { logout(); closeMobileMenu(); }}>
                                                Log out
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                                <Link to='/my-account' className='nav-icon-link nav-icon-mobile-only' onClick={closeMobileMenu}>
                                    <HiOutlineUser />
                                </Link>
                            </>
                        ) : (
                            <Link to='/sign-up' className='nav-icon-link' onClick={closeMobileMenu}>
                                <HiOutlineUser />
                            </Link>
                        )}
                        <div className='nav-icon-link' onClick={toggleSearch}>
                            <FiSearch />
                        </div>
                        <div className='nav-icon-link cart-icon-wrapper' onClick={() => { closeMobileMenu(); openDrawer(); }}>
                            <FiShoppingBag />
                            {cartCount > 0 && <span className='cart-badge'>{cartCount}</span>}
                        </div>
                    </div>
                </div>
            </nav>
            {searchOpen && (
                <div ref={searchRef} className="search-bar-container">
                    <div className="search-bar-inner">
                        <div className="search-input-container">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                autoFocus
                            />
                            <FiSearch className="search-icon-inside" />
                        </div>
                        <button onClick={toggleSearch} className="close-search-btn">X</button>
                    </div>
                    {searchQuery.trim() !== '' && (
                        <div className="search-results-dropdown">
                            {searchResults.length > 0 ? (
                                searchResults.map((item) => (
                                    <div
                                        key={item.path}
                                        className="search-result-item"
                                        onClick={() => handleResultClick(item.path)}
                                    >
                                        <span className="search-result-title">{item.title}</span>
                                        <span className="search-result-desc">{item.description}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="search-no-results">No results found</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default Navbar;
